import { useCallback, useMemo } from 'react';
import { filter, map, Subject } from 'rxjs';
import { match } from 'ts-pattern';
import { deleteLetterNew, sendLetter } from '../../api/Drafts';
import { useAuthenticateApiCall } from '../hooks/useAuthenticateApiCall';
import { useSubscription } from '../hooks/useSubscription';
import { BusDispatch, BusEvent } from './EditorContext';
import { ComposerEvent } from './useComposerState';

const useSendLetter = (dispatch: BusDispatch, id: string) => {
  const authedSendLetter = useAuthenticateApiCall(sendLetter);

  return useCallback(async () => {
    const { status, postSendAction, errorId } = await authedSendLetter(id);

    if (status === 'ok' && postSendAction) {
      match(postSendAction)
        .with('none', () => {
          dispatch({ type: 'Notify', event: 'LetterSent' });
          dispatch({ type: 'Navigator', event: 'Root' });
        })
        .with('redirectToInvitations', () => {
          dispatch({ type: 'Notify', event: 'InvitationalLetterPrepared' });
          dispatch({ type: 'Navigator', event: 'Invitations' });
        })
        .exhaustive();
    } else if (errorId) {
      match(errorId)
        // TODO: Clear the composer
        .with('rate-limit', () => {
          dispatch({ type: 'Notify', event: 'RateLimit' });
          dispatch({ type: 'Composer', event: { type: 'CLEAR' } });
        })

        .otherwise(() => {
          dispatch({ type: 'Notify', event: 'SomethingWentWrong' });
          dispatch({ type: 'Composer', event: { type: 'CLEAR' } });
        });
    }
  }, [authedSendLetter, dispatch, id]);
};

const useDeleteLetter = (dispatch: BusDispatch, id: string) => {
  const authedDeleteLetter = useAuthenticateApiCall(deleteLetterNew);

  return useCallback(async () => {
    const { status } = await authedDeleteLetter(id);

    match(status)
      .with('ok', () => {
        dispatch({ type: 'Notify', event: 'DraftDeleted' });
        dispatch({ type: 'Navigator', event: 'Root' });
      })
      .otherwise(() => {
        dispatch({ type: 'Notify', event: 'SomethingWentWrong' });
        dispatch({ type: 'Composer', event: { type: 'CLEAR' } });
      });
  }, [authedDeleteLetter, dispatch, id]);
};

export const useSendDelete = (bus$: Subject<BusEvent>, dispatch: BusDispatch, id: string) => {
  const composerEvent$ = useMemo(
    () =>
      bus$.pipe(
        filter((e) => e.type === 'Composer'),
        map((e) => (e as { type: 'Composer'; event: ComposerEvent }).event)
      ),
    [bus$]
  );

  const onSendLetter = useSendLetter(dispatch, id);
  const onDeleteLetter = useDeleteLetter(dispatch, id);

  useSubscription(
    () =>
      composerEvent$.subscribe((event) => {
        match(event.type)
          .with('SEND_LETTER', onSendLetter)
          .with('DELETE_LETTER', onDeleteLetter)
          .otherwise(() => {});
      }),
    [composerEvent$, onSendLetter, onDeleteLetter]
  );
};
